import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import FacilityCard from '../components/FacilityCard';
import { Phone, MapPin, Users, TrendingUp, AlertTriangle, ChevronRight, Loader2 } from 'lucide-react';
import { facilityApi, handleApiError } from '../utils/api';
import { mockStates } from '../data/mock'; // Keep for state info and fallback
import { toSafeJsonLdString } from '../utils/jsonLd';

const StatePage = () => {
  const { stateCode } = useParams();
  
  // State for API data
  const [loading, setLoading] = useState(true);
  const [facilities, setFacilities] = useState([]);
  const [stateStats, setStateStats] = useState(null);
  const [error, setError] = useState(null);
  
  // Find state info by either code or name (case-insensitive)
  // Also handle URL-friendly formats like "new-york" -> "New York"
  const normalizeStateName = (name) => {
    return name.split('-').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    ).join(' ');
  };
  
  const stateInfo = mockStates.find(s => {
    const normalizedStateCode = normalizeStateName(stateCode || '');
    return (
      s.code.toLowerCase() === stateCode?.toLowerCase() || 
      s.name.toLowerCase() === stateCode?.toLowerCase() ||
      s.name.toLowerCase() === normalizedStateCode.toLowerCase()
    );
  });
  
  // Get the actual state code for API calls
  const stateCodeUpper = stateInfo?.code || stateCode?.toUpperCase();

  // Fetch state facilities and statistics
  useEffect(() => {
    const fetchStateData = async () => {
      if (!stateCodeUpper) return;
      
      setLoading(true);
      setError(null);
      
      try {
        // Fetch facilities for this state
        const facilityResponse = await facilityApi.getByState(stateCodeUpper, {
          limit: 100 // Get more facilities for state page
        });
        
        console.log('Facility API Response:', facilityResponse);
        
        setFacilities(facilityResponse.facilities || []);
        
        // Create state-specific stats
        const stateStatistics = {
          facilities: facilityResponse.total || facilityResponse.facilities?.length || 0,
          overdoseDeaths: "N/A", // This would come from external data source
          peopleNeedingTreatment: "N/A", // This would come from external data source
          helpline: "1-888-968-1014" // Updated helpline number
        };
        
        setStateStats(stateStatistics);
        
      } catch (err) {
        console.error('Error fetching state data:', err);
        setError(err.message);
        
        // Set fallback data
        setFacilities([]);
        setStateStats({
          facilities: 0,
          overdiseDeaths: "N/A",
          peopleNeedingTreatment: "N/A",
          helpline: "1-888-968-1014"
        });
      } finally {
        setLoading(false);
      }
    };

    fetchStateData();
  }, [stateCodeUpper]);

  if (!stateInfo) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="text-center py-12">
            <h1 className="text-2xl font-bold text-gray-900 mb-4">State Not Found</h1>
            <p className="text-gray-600 mb-6">
              We couldn't find information for the state code "{stateCode}".
            </p>
            <Button asChild>
              <Link to="/states">Browse All States</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="text-center py-12">
            <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-blue-600" />
            <h2 className="text-lg font-semibold text-gray-900 mb-2">Loading Treatment Centers</h2>
            <p className="text-gray-600">Finding facilities in {stateInfo.name}...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Use fetched data or fallback
  const currentStateStats = stateStats || {
    facilities: 0,
    overdoseDeaths: "N/A",
    peopleNeedingTreatment: "N/A",
    helpline: "1-888-968-1014"
  };

  // SEO structured data
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": `${stateInfo.name} Addiction Treatment Centers`,
    "description": `Find addiction treatment centers in ${stateInfo.name}. Browse ${facilities.length} facilities offering comprehensive recovery services.`,
    "url": `https://addictionrehabcall.com/states/${stateCode}`,
    "about": {
      "@type": "Place",
      "name": stateInfo.name,
      "geo": {
        "@type": "GeoCoordinates"
      }
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* SEO Head */}
      <title>{stateInfo.name} Addiction Treatment Centers | Addiction Rehab Call</title>
      <meta 
        name="description" 
        content={`Find the best addiction treatment centers in ${stateInfo.name}. Browse ${facilities.length} accredited facilities offering inpatient, outpatient, and detox services.`} 
      />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-600 to-purple-600 text-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              Addiction Treatment Centers in {stateInfo.name}
            </h1>
            <p className="text-xl text-blue-100 mb-8">
              Find trusted addiction recovery facilities near you in {stateInfo.name}. 
              {facilities.length > 0 && ` Browse ${facilities.length} treatment centers`} offering comprehensive care and support.
            </p>
            
            {/* Emergency Helpline */}
            <div className="inline-flex items-center bg-white/10 backdrop-blur-sm rounded-full px-6 py-4 mb-8">
              <Phone className="h-5 w-5 mr-3" />
              <span className="mr-2">24/7 {stateInfo.name} Helpline:</span>
              <a 
                href={`tel:${currentStateStats.helpline}`}
                className="text-xl font-bold hover:underline"
              >
                {currentStateStats.helpline}
              </a>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild size="lg" className="bg-white text-blue-600 hover:bg-gray-100">
                <Link to={`/search?state=${stateCodeUpper}`}>
                  <MapPin className="h-5 w-5 mr-2" />
                  Find Treatment Centers
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="border-white text-white hover:bg-white hover:text-blue-600">
                <a href={`tel:${currentStateStats.helpline}`}>
                  <Phone className="h-5 w-5 mr-2" />
                  Call for Help Now
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* State Statistics */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-2xl font-bold text-center mb-8">
              {stateInfo.name} Addiction Statistics & Resources
            </h2>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              <Card className="text-center border-0 shadow-md">
                <CardContent className="pt-8 pb-6">
                  <div className="text-3xl font-bold text-blue-600 mb-2">
                    {currentStateStats.facilities}
                  </div>
                  <div className="text-gray-600">Treatment Centers</div>
                </CardContent>
              </Card>
              
              <Card className="text-center border-0 shadow-md">
                <CardContent className="pt-8 pb-6">
                  <div className="text-3xl font-bold text-purple-600 mb-2">
                    {stateInfo.population.toLocaleString()}
                  </div>
                  <div className="text-gray-600">Population</div>
                </CardContent>
              </Card>
              
              <Card className="text-center border-0 shadow-md">
                <CardContent className="pt-8 pb-6">
                  <div className="text-3xl font-bold text-orange-600 mb-2">
                    {currentStateStats.peopleNeedingTreatment}
                  </div>
                  <div className="text-gray-600">Need Treatment</div>
                </CardContent>
              </Card>
              
              <Card className="text-center border-0 shadow-md">
                <CardContent className="pt-8 pb-6">
                  <div className="text-3xl font-bold text-red-600 mb-2">
                    {currentStateStats.overdoseDeaths}
                  </div>
                  <div className="text-gray-600">Overdose Deaths (2023)</div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Treatment Centers */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Treatment Centers in {stateInfo.name}
              </h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                Browse addiction treatment facilities in {stateInfo.name} offering various 
                levels of care including inpatient, outpatient, and detoxification services.
              </p>
            </div>

            {facilities.length > 0 ? (
              <>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-8">
                  {facilities.slice(0, 6).map((facility) => (
                    <FacilityCard 
                      key={facility.id} 
                      facility={facility} 
                      showState={false} 
                    />
                  ))}
                </div>
                
                {facilities.length > 6 && (
                  <div className="text-center">
                    <Button asChild size="lg" variant="outline">
                      <Link to={`/search?state=${stateCodeUpper}`}>
                        View All {facilities.length} Facilities
                        <ChevronRight className="h-4 w-4 ml-2" />
                      </Link>
                    </Button>
                  </div>
                )}
              </>
            ) : (
              <Card>
                <CardContent className="text-center py-12">
                  <MapPin className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    No facilities listed yet
                  </h3>
                  <p className="text-gray-600 mb-6">
                    We're working to add treatment facilities in {stateInfo.name}. 
                    In the meantime, call our helpline for assistance finding treatment.
                  </p>
                  <Button asChild>
                    <a href={`tel:${currentStateStats.helpline}`}>
                      <Phone className="h-4 w-4 mr-2" />
                      Call for Help: {currentStateStats.helpline}
                    </a>
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </section>

      {/* Local Resources */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-center mb-8">
              {stateInfo.name} Addiction Resources & Support
            </h2>
            
            <div className="grid md:grid-cols-2 gap-8">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <Phone className="h-5 w-5 mr-2 text-blue-600" />
                    Emergency & Crisis Support
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <h4 className="font-medium">National Suicide Prevention Lifeline</h4>
                    <a href="tel:988" className="text-blue-600 hover:underline">988</a>
                  </div>
                  <div>
                    <h4 className="font-medium">SAMHSA National Helpline</h4>
                    <a href="tel:1-800-662-4357" className="text-blue-600 hover:underline">
                      1-800-662-HELP (4357)
                    </a>
                  </div>
                  <div>
                    <h4 className="font-medium">Find a Rehab Center In {stateInfo.name}</h4>
                    <a href={`tel:${currentStateStats.helpline}`} className="text-blue-600 hover:underline">
                      {currentStateStats.helpline}
                    </a>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <Users className="h-5 w-5 mr-2 text-purple-600" />
                    Support Groups & Meetings
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <h4 className="font-medium">Alcoholics Anonymous (AA)</h4>
                    <p className="text-sm text-gray-600">Find local AA meetings in {stateInfo.name}</p>
                  </div>
                  <div>
                    <h4 className="font-medium">Narcotics Anonymous (NA)</h4>
                    <p className="text-sm text-gray-600">Locate NA groups near you</p>
                  </div>
                  <div>
                    <h4 className="font-medium">SMART Recovery</h4>
                    <p className="text-sm text-gray-600">Science-based addiction recovery</p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-blue-600 to-purple-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">
            Ready to Start Recovery in {stateInfo.name}?
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Don't wait another day. Connect with treatment centers in {stateInfo.name} 
            and take the first step toward a healthier, sober life.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg" className="bg-white text-blue-600 hover:bg-gray-100">
              <a href={`tel:${currentStateStats.helpline}`}>
                <Phone className="h-5 w-5 mr-2" />
                Call Now: {currentStateStats.helpline}
              </a>
            </Button>
            <Button asChild variant="outline" size="lg" className="border-white text-white hover:bg-white hover:text-blue-600">
              <Link to="/get-help">
                Get Help Online
                <ChevronRight className="h-4 w-4 ml-2" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Structured Data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: toSafeJsonLdString(structuredData)
        }}
      />
    </div>
  );
};

export default StatePage;